import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'tdesign-react';

import NovaDialog from '@/components/dialog';
import { MultiCharacter, ThumbnailType } from '@/interface/media';

import { getVideoThumbnail } from '../../core/player/utils/getThumbnail';
import { UploadCharacter } from '..';
import CustomKonva, { IKonvaRef, INIT_COORDINATE, INIT_SCALE, KonvaImage } from '../konva';
import styles from './index.module.less';

interface IProps {
  value: MultiCharacter[];
  visible: boolean;
  disabled: boolean;
  thumbnail?: ThumbnailType;
  onChange: (value: MultiCharacter[]) => void;
  onClose: () => void;
}

const CharacterDialog: React.FC<IProps> = memo(({
  value,
  visible,
  disabled,
  thumbnail,
  onChange,
  onClose,
}) => {
  const [background, setBackground] = useState<string>('');
  const konvaRef = useRef<IKonvaRef>(null);

  useEffect(() => {
    (async () => {
      const url = thumbnail?.srcType === 'image'
        ? thumbnail?.url
        : await getVideoThumbnail(thumbnail?.url || '', 0);
      setBackground(url || '');
    })();
  }, [thumbnail?.srcType, thumbnail?.url]);

  const handleUpload = (val: MultiCharacter[]) => {
    onChange(val);
  };

  const handleConfirm = () => {
    if (!konvaRef.current || disabled) return;

    const konvaImages = konvaRef.current.getImages();
    const newValue = value.map((char) => {
      const newChar = konvaImages.find(img => img.id === char.id);
      return newChar
        ? {
          ...char,
          coordinate: {
            x: newChar.x || INIT_COORDINATE,
            y: newChar.y || INIT_COORDINATE,
          },
          scale: newChar.scale || INIT_SCALE,
        }
        : char;
    });

    onChange(newValue);
    onClose();
  };

  const konvaImages: KonvaImage[] = useMemo(() => value?.map(e => ({
    id: e.id || '',
    url: e.image?.url?.url
      || e.spine?.thumbnail
      || '',
    x: e.coordinate?.x,
    y: e.coordinate?.y,
    scale: e.scale,
  })), [value]);

  const getHeader = () => (
    <div className={styles.modalTitle}>
      <span style={{ flexGrow: 1 }}>编辑多人立绘</span>
    </div>
  );

  const dialogConfig = {
    visible,
    closeBtn: false,
    className: styles.dialog,
    width: 'fit-content',
    height: 'fit-content',
    onCancel: onClose,
    destroyOnClose: true,
    header: getHeader(),
    confirmBtn: <Button
      theme='primary'
      className={styles.confirmBtn}
      disabled={disabled}
      onClick={handleConfirm}>
      保存
    </Button>,
  };

  return (
    <NovaDialog dialogConfig={dialogConfig}>
      <div className={styles.wrapper}>
        <div className={styles.left}>
          <div className={styles.text}>图层顺序</div>
          <div className={styles.display}>
            <UploadCharacter
              value={value}
              disabled={disabled}
              onChange={handleUpload}
            />
          </div>
        </div>
        <div className={styles.right}>
          <CustomKonva
            konvaRef={konvaRef}
            value={konvaImages}
            disabled={disabled}
            background={background}
          />
        </div>
      </div>
    </NovaDialog>
  );
});

export default CharacterDialog;
